import { Factory, Users, Award, Globe } from 'lucide-react';
import SEO from '@/components/SEO';

const About = () => {
  const values = [
    {
      icon: Factory,
      title: 'Quality Manufacturing',
      description:
        'Every furnace is built to exacting standards with premium materials and rigorous in-house testing at our Amritsar and Ludhiana facilities.',
    },
    {
      icon: Users,
      title: 'Expert Team',
      description:
        'Experienced metallurgists, electrical engineers and fabricators working under one roof to deliver furnaces that meet specification on day one.',
    },
    {
      icon: Award,
      title: 'ISO 9001 Certified',
      description:
        'Quality management systems certified to ISO 9001 — backed by over 1000 successful installations across diverse Indian industries.',
    },
    {
      icon: Globe,
      title: 'Pan India Reach',
      description:
        'Serving clients across the country with local commissioning support, responsive service and in-house spares manufacturing.',
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        path="/about"
        title="About Arkotherm Furnaces — 60+ Years of Industrial Furnace Manufacturing"
        description="Since 1960, Arkotherm Furnaces has been a pioneer manufacturer of industrial heat-treatment furnaces. ISO 9001 Certified, with manufacturing facilities at Amritsar and Ludhiana, serving Indian industry for six decades."
      />
      {/* Hero */}
      <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-block px-4 py-2 bg-orange-500/20 border border-orange-500 rounded-full text-sm font-semibold mb-4">
              About Us · ISO 9001 Certified Company
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold mb-4">
              Building Excellence in <span className="gradient-text">Industrial Heat</span>
            </h1>
            <p className="text-xl text-gray-300">
              Since 1960, Arkotherm has been at the forefront of industrial furnace technology,
              delivering precision heat-treatment solutions to manufacturers across India.
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=600&h=500&fit=crop"
                alt="Arkotherm Factory"
                className="rounded-2xl shadow-2xl"
              />
            </div>
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Our <span className="gradient-text">Story</span>
              </h2>
              <div className="space-y-4 text-lg text-gray-700 leading-relaxed">
                <p>
                  We are proud to introduce ourselves as pioneer manufacturers of heat-treatment furnaces
                  and allied products since 1960. Our range includes furnaces for hardening, tempering,
                  normalising, annealing, gas carburizing, and the melting of non-ferrous metals — all
                  designed, fabricated and commissioned in-house.
                </p>
                <p>
                  From our manufacturing facilities at <span className="font-semibold">Amritsar and
                  Ludhiana</span>, we serve a broad cross-section of Indian industry — cycle and auto-parts
                  manufacturers, bearing makers, valves, gears, tractor-component plants, fasteners and
                  hand-tool producers.
                </p>
                <p>
                  Quality has kept us in business for six decades. Our products are built with utmost
                  care, premium raw material and an ISO 9001 Certified quality-management system —
                  giving customers furnaces that perform on day one and stay in service for decades.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our <span className="gradient-text">Values</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              The principles that guide every project and relationship.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div key={index} className="bg-white rounded-xl p-6 shadow-lg hover-lift">
                  <Icon className="text-orange-500 mb-4" size={40} />
                  <h4 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h4>
                  <p className="text-gray-700 leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="section-padding dark-gradient text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {[
              { value: '65+', label: 'Years Experience' },
              { value: '1000+', label: 'Installations' },
              { value: '2', label: 'Manufacturing Plants' },
              { value: 'ISO 9001', label: 'Certified' },
            ].map((stat, index) => (
              <div key={index}>
                <div className="text-5xl font-bold gradient-text mb-2">{stat.value}</div>
                <div className="text-gray-300 text-lg">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-gradient-to-r from-orange-500 to-yellow-500">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">Join our growing list of satisfied clients</h2>
          <p className="text-xl text-white/90 mb-8">
            Experience the Arkotherm difference — precision, reliability, and support you can count on.
          </p>
          <a
            href="/contact"
            className="inline-block px-8 py-4 bg-white text-orange-600 font-bold rounded-full hover:shadow-xl transition-all duration-300"
            data-testid="about-cta-btn"
          >
            Work With Us
          </a>
        </div>
      </section>
    </div>
  );
};

export default About;
